import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PACKAGING_CATEGORY_LABELS_IT,
  packagingItemCreateRequestSchema,
  type PackagingItemCreateRequest,
} from '@produzionejb/types';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@produzionejb/ui';
import { ApiError } from '../../lib/api-client';
import {
  useCreatePackagingItem,
  usePackagingItem,
  useUpdatePackagingItem,
} from '../../lib/packaging-items-api';

const DEFAULT_VALUES: PackagingItemCreateRequest = {
  code: '',
  name: '',
  category: 'OTHER',
  unit: 'pz',
  active: true,
};

export function PackagingItemFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const itemQ = usePackagingItem(id);
  const createMut = useCreatePackagingItem();
  const updateMut = useUpdatePackagingItem(id ?? '');

  const form = useForm<PackagingItemCreateRequest>({
    resolver: zodResolver(packagingItemCreateRequestSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (isEdit && itemQ.data) {
      form.reset({
        code: itemQ.data.code,
        name: itemQ.data.name,
        category: itemQ.data.category,
        unit: itemQ.data.unit,
        active: itemQ.data.active,
      });
    }
  }, [isEdit, itemQ.data, form]);

  async function onSubmit(values: PackagingItemCreateRequest) {
    try {
      if (isEdit && id) {
        await updateMut.mutateAsync(values);
      } else {
        await createMut.mutateAsync(values);
      }
      navigate('/packaging-items');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        form.setError('code', { message: 'Codice packaging gia esistente' });
      } else {
        window.alert((err as Error).message);
      }
    }
  }

  const isSubmitting = createMut.isPending || updateMut.isPending;

  if (isEdit && itemQ.isLoading) {
    return <p className="text-sm text-slate-500">Caricamento...</p>;
  }
  if (isEdit && itemQ.error) {
    return (
      <p className="text-sm text-red-600">
        Errore: {(itemQ.error as Error).message}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">
          {isEdit ? 'Modifica packaging' : 'Nuovo packaging'}
        </h2>
        <Button variant="secondary" onClick={() => navigate('/packaging-items')}>
          Indietro
        </Button>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Card>
          <CardHeader>
            <CardTitle>Anagrafica</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="code">Codice *</Label>
              <Input id="code" autoComplete="off" {...form.register('code')} />
              {form.formState.errors.code && (
                <p className="text-xs text-red-600">{form.formState.errors.code.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="name">Nome *</Label>
              <Input id="name" autoComplete="off" {...form.register('name')} />
              {form.formState.errors.name && (
                <p className="text-xs text-red-600">{form.formState.errors.name.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="category">Categoria *</Label>
              <select
                id="category"
                className="block h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                {...form.register('category')}
              >
                {Object.entries(PACKAGING_CATEGORY_LABELS_IT).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="unit">Unita di misura</Label>
              <Input
                id="unit"
                placeholder="pz, m, kg..."
                {...form.register('unit')}
              />
              {form.formState.errors.unit && (
                <p className="text-xs text-red-600">{form.formState.errors.unit.message}</p>
              )}
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300"
                  {...form.register('active')}
                />
                <span className="text-sm text-slate-700">Attivo</span>
              </label>
              <p className="text-xs text-slate-500">
                Se disattivato non comparira nei picker (CON/MSS/INV/BOM).
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            type="button"
            onClick={() => navigate('/packaging-items')}
            disabled={isSubmitting}
          >
            Annulla
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isEdit ? 'Salva' : 'Crea'}
          </Button>
        </div>
      </form>
    </div>
  );
}
